import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Slide,
  Link,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, LoginRounded } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { mobileAuthService } from '../../../services/mobile/mobileAuthService';

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const LoginContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #3b82f6 100%)',
  padding: '16px'
}));

const LoginCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.97)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  boxShadow: '0 20px 60px rgba(37, 99, 235, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  maxWidth: '420px',
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    height: 56,
    transition: 'all 0.2s ease',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#93c5fd'
      }
    },
    '&.Mui-focused': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2563eb'
      }
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#2563eb'
  }
}));

const MobileLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await mobileAuthService.login({ email, password });

      if (result.success) {
        if (result.requiresOtp) {
          navigate('/mobile/verify-otp', {
            state: { email, password }
          });
        } else {
          // Direct login (trusted device) — token already stored by service
          navigate('/mobile');
        }
      } else {
        setError(result.message || 'Invalid credentials.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
            <span style={{ color: '#0f172a' }}>Easy</span>
            <span style={{ color: '#2563eb' }}>Check</span>
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Sign in to your account
          </Typography>
        </Box>

        <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)'
            }}
          >
            {error}
          </Alert>
        </Slide>

        <form onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            label="Email"
            type="email"
            inputProps={{ inputMode: 'email' }}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
          />
          <StyledTextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ textAlign: 'right', mb: 2.5 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate('/reset-password')}
              sx={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot Password?
            </Link>
          </Box>

          <EasyCheckButton
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.75,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8, #4338ca)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)'
              },
              transition: 'all 0.2s ease',
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoginRounded sx={{ fontSize: 20 }} />
                Sign In
              </Box>
            )}
          </EasyCheckButton>
        </form>

        <Divider sx={{ my: 3, color: '#94a3b8', fontSize: '0.75rem' }}>or</Divider>

        <Typography variant="body2" sx={{ textAlign: 'center', color: '#64748b' }}>
          Don&apos;t have an account?{' '}
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/signup')}
            sx={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Sign up
          </Link>
        </Typography>
      </LoginCard>
    </LoginContainer>
  );
};

export default MobileLogin;
